
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function HomeEVChargingStationsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Home EV Charging Station Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Complete home EV charging station installation in Fresno, CA. All brands supported including ChargePoint, JuiceBox, Grizzl-E, and more.',
    url: `${siteUrl}/services/renewables-ev/home-ev-charging-stations`,
  };

  return (
    <>
      <SEOHead
        title="Home EV Charging Station Installation Fresno CA | All Brands"
        description="Professional home EV charging station installation in Fresno, CA. ChargePoint, JuiceBox, Grizzl-E, and all major brands. Federal tax credits available."
        canonical="/services/renewables-ev/home-ev-charging-stations"
        keywords="home EV charging station Fresno, ChargePoint installation, JuiceBox charger, residential EV charging"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Home EV Charging Stations — Fresno, CA"
        heroSubtitle="Professional installation for all EV charging brands"
        heroImage="https://public.readdy.ai/ai/img_res/6aeca1d3a841667120ddebfc87fe2778.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20ChargePoint%20Home%20Flex%20charging%20station%20mounted%20on%20garage%20wall%20with%20digital%20display%20showing%20charging%20status%20and%20WiFi%20indicator%20with%20electrical%20conduit%20and%20junction%20box%20visible%20professional%20residential%20installation%20California%20no%20people&width=700&height=600&seq=home-ev-stations-fresno-001&orientation=portrait"
        fresnoContent="Fresno homeowners are rapidly adopting electric vehicles, creating demand for reliable home charging solutions. Our certified electricians install all major EV charging brands including ChargePoint, JuiceBox, Grizzl-E, Emporia, and Wallbox, ensuring compatibility with every electric vehicle from Tesla to Ford to Rivian. With Fresno's hot summers and long commutes, having a dependable home charging station is essential. We serve all of Fresno County including Clovis, Sanger, and surrounding Central Valley communities, helping you choose the perfect charger and maximize federal tax credits and California rebates."
        features={[
          {
            icon: 'ri-charging-pile-line',
            title: 'All Brands Supported',
            description: 'ChargePoint, JuiceBox, Grizzl-E, Emporia, Wallbox, and more',
          },
          {
            icon: 'ri-plug-line',
            title: 'Universal Compatibility',
            description: 'Works with all electric vehicles using J1772 standard',
          },
          {
            icon: 'ri-smartphone-line',
            title: 'Smart Charging Features',
            description: 'WiFi-enabled chargers with mobile app control and scheduling',
          },
          {
            icon: 'ri-timer-line',
            title: 'Scheduled Charging',
            description: 'Charge during off-peak hours to save on PG&E rates',
          },
          {
            icon: 'ri-bar-chart-line',
            title: 'Energy Monitoring',
            description: 'Track charging costs and energy usage through apps',
          },
          {
            icon: 'ri-shield-check-line',
            title: 'Weather Resistant',
            description: 'Indoor and outdoor rated chargers for Fresno climate',
          },
        ]}
        whatsIncluded={[
          'Free consultation to select the best charger for your needs',
          'Complete electrical assessment and load calculation',
          'City of Fresno building permits and inspections',
          'Professional Level 2 charging station installation',
          'Dedicated 40-50 amp circuit from electrical panel',
          'Indoor or outdoor mounting with weather protection',
          'Cable management system and cord holder',
          'WiFi setup and mobile app configuration',
          'Scheduled charging setup for off-peak PG&E rates',
          'Energy monitoring dashboard configuration',
          'Federal tax credit documentation (up to $1,000)',
          'California rebate application assistance',
          'Complete testing and owner training',
          '2-year installation warranty',
        ]}
        faqs={[
          {
            question: 'Which home EV charging station is best for Fresno residents?',
            answer:
              "The best charger depends on your specific needs. ChargePoint Home Flex is popular for its reliability and smart features. JuiceBox offers excellent value with robust WiFi connectivity. Grizzl-E is ideal for outdoor installations in Fresno's hot summers with its durable construction. Emporia provides detailed energy monitoring. We recommend chargers based on your vehicle, budget, desired features, and installation location. During our free consultation, we assess your needs and recommend 2-3 options with pros and cons for each.",
          },
          {
            question: 'How much does home EV charging station installation cost in Fresno?',
            answer:
              'Home EV charging station installation in Fresno typically costs $1,000-$2,500 including the charger unit ($400-$700), dedicated circuit, permits, and labor. ChargePoint and JuiceBox installations average $1,200-$1,800, while premium models like Wallbox Pulsar Plus cost $1,500-$2,200. Panel upgrades add $1,500-$3,000 if needed. However, federal tax credits cover 30% of costs (up to $1,000), and California rebates provide additional savings. Most Fresno homeowners pay $700-$1,500 net after incentives.',
          },
          {
            question: 'Can I install an EV charging station outdoors in Fresno?',
            answer:
              "Yes! Many EV charging stations are rated for outdoor installation and can handle Fresno's hot summers and occasional rain. Models like Grizzl-E, ChargePoint Home Flex, and JuiceBox 40 have NEMA 4 ratings for outdoor use. We install weatherproof enclosures and proper conduit to protect wiring from Fresno's intense summer heat and UV exposure. Outdoor installations are perfect for carports, driveways, or homes without attached garages. We ensure all outdoor installations meet NEC requirements for weather protection and safety.",
          },
          {
            question: 'Do home EV charging stations work with all electric vehicles?',
            answer:
              'Yes! All Level 2 home charging stations use the universal J1772 connector standard, which works with every electric vehicle sold in North America except older Teslas (which include a J1772 adapter). This means your ChargePoint, JuiceBox, or Grizzl-E charger works with Ford F-150 Lightning, Chevy Bolt, Nissan Leaf, Hyundai Ioniq, Rivian, and all other EVs. If you own a Tesla, you can use any J1772 charger with the included adapter, or we can install a dedicated Tesla Wall Connector.',
          },
          {
            question: 'Can I schedule charging to save money on PG&E rates in Fresno?',
            answer:
              "Absolutely! Most smart EV charging stations let you schedule charging during off-peak hours when PG&E rates are lowest. PG&E's EV rate plan offers electricity for as low as $0.26/kWh during off-peak hours (midnight-3pm) versus $0.51/kWh during peak hours (4-9pm). By scheduling your EV to charge overnight, you can cut charging costs in half. We configure your charger's app to automatically start charging during the cheapest hours, maximizing your savings without any daily effort.",
          },
          {
            question: 'How long does home EV charging station installation take in Fresno?',
            answer:
              'Most home EV charging station installations in Fresno are completed in 4-6 hours. This includes running the dedicated circuit from your electrical panel, mounting the charger, WiFi setup, and testing. Outdoor installations or complex conduit runs may take a full day. If panel upgrades are needed, add 1-2 days. City of Fresno permit processing takes 1-2 weeks, but we handle all paperwork and inspections. We schedule installations at your convenience and complete most jobs in a single visit.',
          },
        ]}
        ctaText="Get Your Free Charging Station Quote"
      />
    </>
  );
}
